import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { SectionHeader } from '../components/common/SectionHeader';
import { CTASection } from '../components/common/CTASection';
import { LogoCarousel } from '../components/common/LogoCarousel';
import { GeometricBackground } from '../components/decorations/GeometricBackground';
import { certificationsData } from '../data/certifications';

const CertificationsPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-hero-pattern overflow-hidden">
        <GeometricBackground />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <Badge className="mb-4 bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20">
              Certified Excellence
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              Our Certifications
            </h1>
            <p className="text-lg text-primary-foreground/80">
              Industry-recognized credentials that demonstrate our commitment to excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Statement - EXACT */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Award className="w-8 h-8 text-primary" />
            </div>
            <p className="text-xl text-foreground leading-relaxed">
              {certificationsData.statement}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Certifications Carousel */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <SectionHeader
            subtitle="Certified Partners"
            title="Our Certification Portfolio"
            className="mb-12"
          />
          <LogoCarousel logos={certificationsData.certifications} />
        </div>
      </section>

      {/* Certifications List - EXACT */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionHeader
            subtitle="Full List"
            title="All Certifications"
            className="mb-12"
          />

          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {certificationsData.certifications.map((cert, index) => (
              <motion.div
                key={cert.name}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
              >
                <Card className="h-full hover:shadow-lg hover:border-primary/30 transition-all">
                  <CardContent className="p-4 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span className="font-medium text-foreground">{cert.name}</span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Statement - EXACT */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <Card className="border-l-4 border-l-primary">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold text-foreground mb-4">
                  Continuous Training & Development
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {certificationsData.trainingStatement}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Work With Certified Experts"
        description="Partner with a team that brings certified expertise to every project."
        primaryButtonText="Contact Us"
        primaryButtonLink="/contact"
      />
    </div>
  );
};

export default CertificationsPage;
